import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { ErrorService } from '../error';
import { LoadingObject, LoadingService } from '../loaders';
import { catchError, finalize, map, Observable, of, throwError } from 'rxjs';
import {
  UserSettingName,
  UserSettingResource,
  UserSettingsListResponse,
  UserSettingSingleResponse,
} from './user-settings.types';

export type UserSettingsMap = Record<string, string | null>;

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private readonly http = inject(HttpClient);
  private readonly loadingService = inject(LoadingService);

  private readonly _settings = signal<UserSettingsMap>({});
  private readonly _loadingObject = signal<LoadingObject>({});

  constructor(private errorService: ErrorService) {}

  clearData(): void {
    this._settings.set({});
    this._loadingObject.set({});
  }

  get settings(): UserSettingsMap {
    return this._settings();
  }

  get settingsSignal(): WritableSignal<UserSettingsMap> {
    return this._settings;
  }

  get loadingObject(): LoadingObject {
    return this._loadingObject();
  }

  get loadingObjectSignal(): WritableSignal<LoadingObject> {
    return this._loadingObject;
  }

  setLoadingStatus(key: string, status: boolean): void {
    this._loadingObject.set(this.loadingService.setLoadingStatus(key, status, this._loadingObject()));
  }

  getSettings(freshFromServer = false): Observable<UserSettingsMap> {
    if (!freshFromServer && Object.keys(this.settings).length > 0) {
      return of(this.settings);
    }

    this.setLoadingStatus('getSettings', true);

    return this.http.get<UserSettingsListResponse>('api/v1/user/settings').pipe(
      map((response) => {
        const normalisedMap = this.normaliseSettingsToMap(response?.data ?? []);
        this._settings.set(normalisedMap);
        return normalisedMap;
      }),
      catchError((errorResponse: HttpErrorResponse) => this.handleError(errorResponse, 'error.could_not_load')),
      finalize(() => {
        this.setLoadingStatus('getSettings', false);
      }),
    );
  }

  getSetting(settingName: UserSettingName, freshFromServer = false): Observable<string | null> {
    const cachedSettings = this.settings;
    const settingExistsInCache = Object.prototype.hasOwnProperty.call(cachedSettings, settingName);
    if (!freshFromServer && settingExistsInCache) {
      return of(cachedSettings[settingName]);
    }

    this.setLoadingStatus(`getSetting_${settingName}`, true);

    return this.http.get<UserSettingSingleResponse>(`api/v1/user/settings/${settingName}`).pipe(
      map((response) => {
        const settingValue = this.normaliseSettingValue(response?.data?.setting_value);
        this._settings.set({
          ...this.settings,
          [settingName]: settingValue,
        });
        return settingValue;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse.status === 404) {
          this._settings.set({
            ...this.settings,
            [settingName]: null,
          });
          return of(null);
        }
        return this.handleError(errorResponse, 'error.could_not_load');
      }),
      finalize(() => {
        this.setLoadingStatus(`getSetting_${settingName}`, false);
      }),
    );
  }

  createSetting(settingName: UserSettingName, settingValue: string): Observable<string | null> {
    this.setLoadingStatus(`createSetting_${settingName}`, true);

    return this.http
      .post<UserSettingSingleResponse>('api/v1/user/settings', {
        setting_name: settingName,
        setting_value: settingValue,
      })
      .pipe(
        map((response) => {
          const newValue = this.normaliseSettingValue(response?.data?.setting_value);
          this._settings.set({
            ...this.settings,
            [settingName]: newValue,
          });
          return newValue;
        }),
        catchError((errorResponse: HttpErrorResponse) => this.handleError(errorResponse, 'error.not_done_try_again')),
        finalize(() => {
          this.setLoadingStatus(`createSetting_${settingName}`, false);
        }),
      );
  }

  updateSetting(settingName: UserSettingName, settingValue: string): Observable<string | null> {
    this.setLoadingStatus(`updateSetting_${settingName}`, true);

    return this.http
      .patch<UserSettingSingleResponse>(`api/v1/user/settings/${settingName}`, {
        setting_value: settingValue,
      })
      .pipe(
        map((response) => {
          const newValue = this.normaliseSettingValue(response?.data?.setting_value);
          this._settings.set({
            ...this.settings,
            [settingName]: newValue,
          });
          return newValue;
        }),
        catchError((errorResponse: HttpErrorResponse) => this.handleError(errorResponse, 'error.not_done_try_again')),
        finalize(() => {
          this.setLoadingStatus(`updateSetting_${settingName}`, false);
        }),
      );
  }

  upsertSetting(settingName: UserSettingName, settingValue: string): Observable<string | null> {
    this.setLoadingStatus(`upsertSetting_${settingName}`, true);

    return this.http
      .patch<UserSettingSingleResponse>(`api/v1/user/settings/${settingName}`, {
        setting_value: settingValue,
      })
      .pipe(
        map((response) => {
          const newValue = this.normaliseSettingValue(response?.data?.setting_value);
          this._settings.set({
            ...this.settings,
            [settingName]: newValue,
          });
          return newValue;
        }),
        catchError((errorResponse: HttpErrorResponse) => this.handleError(errorResponse, 'error.not_done_try_again')),
        finalize(() => {
          this.setLoadingStatus(`upsertSetting_${settingName}`, false);
        }),
      );
  }

  deleteSetting(settingName: UserSettingName): Observable<true> {
    this.setLoadingStatus(`deleteSetting_${settingName}`, true);

    return this.http.delete<void>(`api/v1/user/settings/${settingName}`).pipe(
      map(() => {
        this._settings.set({
          ...this.settings,
          [settingName]: null,
        });
        return true as const;
      }),
      catchError((errorResponse: HttpErrorResponse) => this.handleError(errorResponse, 'error.not_done_try_again')),
      finalize(() => {
        this.setLoadingStatus(`deleteSetting_${settingName}`, false);
      }),
    );
  }

  private normaliseSettingsToMap(settings: UserSettingResource[]): UserSettingsMap {
    return settings.reduce<UserSettingsMap>((accumulator, setting) => {
      accumulator[setting.setting_name] = this.normaliseSettingValue(setting.setting_value);
      return accumulator;
    }, {});
  }

  private normaliseSettingValue(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    return String(value);
  }

  private handleError(errorResponse: HttpErrorResponse, fallbackMessage = 'error.something_went_wrong') {
    const normalisedHttpError = this.errorService.normaliseHttpError(errorResponse, fallbackMessage);

    let errorMessage = normalisedHttpError.message ?? fallbackMessage;

    const commonErrorMessageTranslationKey = this.errorService.getCommonErrorMessageTranslationKey(errorMessage);
    if (commonErrorMessageTranslationKey) {
      errorMessage = commonErrorMessageTranslationKey;
    } else if (typeof normalisedHttpError.message === 'string' && normalisedHttpError.message.includes('Request latest data then try again.')) {
      errorMessage = 'error.refresh';
    } else if (normalisedHttpError.status === 401) {
      errorMessage = 'authentication.login_required_feature';
    } else if (normalisedHttpError.status === 422) {
      errorMessage = 'error.data_invalid';
    }

    if (normalisedHttpError.errors) {
      return throwError(() => ({
        message: errorMessage,
        errors: normalisedHttpError.errors,
      }));
    }

    if (errorResponse.error?.meta) {
      return throwError(() => ({
        message: errorMessage,
        meta: errorResponse.error.meta,
      }));
    }

    return throwError(() => errorMessage);
  }
}
